import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { BlockEditModal } from "../BlockEditModal";
import { sanitizeToHtml } from "@/components/preview/BlockRenderer";
import styles from "../blocks/blocks.module.scss";

type BackMatterSubType = "glossary" | "index" | "appendix" | "colophon" | "afterword";

const BACK_MATTER_TYPES: BackMatterSubType[] = ["glossary", "index", "appendix", "colophon", "afterword"];

const BACK_MATTER_LABELS: Record<BackMatterSubType, string> = {
  glossary: "Glossary",
  index: "Index",
  appendix: "Appendix",
  colophon: "Colophon",
  afterword: "Afterword",
};

interface GlossaryEntry {
  term: string;
  definition: string;
}

export function BackMatterNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const rawSubType = node.attrs.subType as string;
  const subType: BackMatterSubType = BACK_MATTER_TYPES.includes(rawSubType as BackMatterSubType)
    ? (rawSubType as BackMatterSubType)
    : "glossary";
  const text = (node.attrs.text as string) || "";
  const entries = (node.attrs.entries as GlossaryEntry[]) || [];

  const [isOpen, setIsOpen] = useState(false);
  const [draftSubType, setDraftSubType] = useState<BackMatterSubType>(subType);
  const [draftText, setDraftText] = useState(text);
  const [draftEntries, setDraftEntries] = useState<GlossaryEntry[]>(entries);

  const handleOpen = useCallback(() => {
    setDraftSubType(subType);
    setDraftText(text);
    setDraftEntries(entries.length > 0 ? [...entries] : [{ term: "", definition: "" }]);
    setIsOpen(true);
  }, [subType, text, entries]);

  const handleSave = useCallback(() => {
    const filteredEntries = draftEntries.filter((e) => e.term.trim() || e.definition.trim());
    updateAttributes({
      subType: draftSubType,
      text: draftText,
      entries: draftSubType === "glossary" ? filteredEntries : [],
    });
    setIsOpen(false);
  }, [draftSubType, draftText, draftEntries, updateAttributes]);

  const addEntry = useCallback(() => {
    setDraftEntries((prev) => [...prev, { term: "", definition: "" }]);
  }, []);

  const removeEntry = useCallback((index: number) => {
    setDraftEntries((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateEntry = useCallback((index: number, field: "term" | "definition", value: string) => {
    setDraftEntries((prev) => prev.map((e, i) => i === index ? { ...e, [field]: value } : e));
  }, []);

  return (
    <NodeViewWrapper data-type="back-matter-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div
        className={styles.backMatterBlock}
        onClick={handleOpen}
        style={{ cursor: "pointer" }}
        title="Click to edit back matter"
      >
        <div className={styles.backMatterLabel}>{BACK_MATTER_LABELS[subType]}</div>
        {subType === "glossary" && entries.length > 0 ? (
          <dl className={styles.glossaryList}>
            {entries.map((entry, i) => (
              <div key={i} className={styles.glossaryItem}>
                <dt className={styles.glossaryTerm}>{entry.term}</dt>
                <dd className={styles.glossaryDefinition}>{entry.definition}</dd>
              </div>
            ))}
          </dl>
        ) : text ? (
          <div
            className={styles.backMatterContent}
            dangerouslySetInnerHTML={{ __html: sanitizeToHtml(text) }}
          />
        ) : (
          <div className={styles.backMatterContent}>
            <span style={{ color: "var(--lilia-text-muted, #999)" }}>Click to add content...</span>
          </div>
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title={`Edit ${BACK_MATTER_LABELS[draftSubType]}`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={700}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Type</label>
          <Select value={draftSubType} onValueChange={(val) => setDraftSubType((val || "glossary") as BackMatterSubType)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BACK_MATTER_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{BACK_MATTER_LABELS[type]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {draftSubType === "glossary" ? (
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Entries</label>
            {draftEntries.map((entry, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
                <input
                  type="text"
                  value={entry.term}
                  onChange={(e) => updateEntry(i, "term", e.target.value)}
                  placeholder="Term"
                  style={{ flex: "0 0 180px", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4, fontWeight: 600 }}
                />
                <input
                  type="text"
                  value={entry.definition}
                  onChange={(e) => updateEntry(i, "definition", e.target.value)}
                  placeholder="Definition"
                  style={{ flex: 1, padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
                />
                <button
                  onClick={() => removeEntry(i)}
                  style={{ padding: "8px", fontSize: 13, background: "transparent", border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4, cursor: "pointer", color: "var(--lilia-text-muted, #999)" }}
                  title="Remove entry"
                >
                  &times;
                </button>
              </div>
            ))}
            <button
              onClick={addEntry}
              style={{ padding: "6px 16px", fontSize: 12, color: "var(--lilia-primary, #1976d2)", background: "transparent", border: "1px dashed var(--lilia-border, #ddd)", borderRadius: 4, cursor: "pointer" }}
            >
              + Add Entry
            </button>
          </div>
        ) : (
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Content</label>
            <textarea
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              placeholder={`Enter ${BACK_MATTER_LABELS[draftSubType].toLowerCase()} content...`}
              rows={8}
              autoFocus
              style={{
                width: "100%", padding: "12px", fontSize: 13,
                border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
                resize: "vertical", lineHeight: 1.6,
              }}
            />
          </div>
        )}
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
